import { useMemo, useState } from 'react';
import { Check, FolderPlus, Pencil, Plus, Trash2 } from 'lucide-react';

import type { Tag, TagCategoria } from '@/api/tags';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Spinner } from '@/components/ui/spinner';
import { TagChip } from '@/components/ui/tag-chip';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { escopoPadrao, useEscoposDasTags, useEscritoriosDasTags, useGestaoTags, useTags, useTituloTagsPessoais } from '@/hooks/useTags';
import { agruparPorEscopo } from '@/lib/tags/agrupar';
import { cn } from '@/lib/utils';

import { CategoriaForm, TagForm, type TagFormValores } from './TagForms';

type Edicao =
  | { tipo: 'tag'; tag?: Tag; escritorioId: string | null; equipeId?: string | null; categoriaId: string | null }
  | { tipo: 'categoria'; categoria?: TagCategoria; escritorioId: string | null };

type Exclusao = { tipo: 'tag'; item: Tag } | { tipo: 'categoria'; item: TagCategoria };

function alcanceDaExclusao(tag: Tag | TagCategoria | undefined): string {
  if (tag && 'equipe_id' in tag && tag.equipe_id) return ', para toda a equipe';
  return tag?.escritorio_id ? ', para todo o escritório' : '';
}

interface TagPainelProps {
  /** Ids marcados (as tags do registro, ou as escolhidas para o lote). */
  marcadas: Set<string>;
  onAlternar: (tag: Tag) => void;
  /** Tag recém-criada: o diálogo do registro já a aplica (menos um toque). */
  onCriada?: (tag: Tag) => void;
  /** Delegação de Leitura: só mostra. */
  somenteLeitura?: boolean;
}

/**
 * A lista das tags agrupada por escopo ("Minhas" / "Do escritório") e por categoria,
 * com "Sem categoria" por último (PLANO_TAGS §6), e a gestão inline: criar, editar e
 * excluir tag e categoria. Toque na linha marca ou desmarca.
 *
 * Gestão só online (D12): sem conexão os botões ficam desabilitados com a dica.
 */
export function TagPainel({ marcadas, onAlternar, onCriada, somenteLeitura = false }: TagPainelProps) {
  const { tags, categorias, equipes, isLoading, isError } = useTags();
  const escritorios = useEscritoriosDasTags();
  const tituloPessoal = useTituloTagsPessoais();
  const escoposDaCategoria = useEscoposDasTags();
  const escopos = useEscoposDasTags({ comEquipes: true });
  const { isOnline } = useOnlineStatus();
  const gestao = useGestaoTags();
  const [edicao, setEdicao] = useState<Edicao | null>(null);
  const [exclusao, setExclusao] = useState<Exclusao | null>(null);

  const secoes = useMemo(
    () => agruparPorEscopo(tags, categorias, escritorios, { incluirVazias: true, tituloPessoal, equipes }),
    [tags, categorias, escritorios, tituloPessoal, equipes],
  );
  const podeGerir = !somenteLeitura && isOnline;

  const salvarTag = async (valores: TagFormValores) => {
    if (edicao?.tipo !== 'tag') return;
    if (edicao.tag) {
      await gestao.editarTag.mutateAsync({
        id: edicao.tag.id,
        nome: valores.nome,
        cor: valores.cor,
        categoria_id: valores.categoriaId,
      });
    } else {
      const criada = await gestao.criarTag.mutateAsync({
        nome: valores.nome,
        cor: valores.cor,
        categoria_id: valores.categoriaId,
        escritorio_id: valores.escritorioId,
        equipe_id: valores.equipeId,
      });
      onCriada?.(criada);
    }
    setEdicao(null);
  };

  const salvarCategoria = async (nome: string, escritorioId: string | null) => {
    if (edicao?.tipo !== 'categoria') return;
    if (edicao.categoria) {
      await gestao.editarCategoria.mutateAsync({ id: edicao.categoria.id, nome });
    } else {
      await gestao.criarCategoria.mutateAsync({ nome, escritorio_id: escritorioId });
    }
    setEdicao(null);
  };

  const confirmarExclusao = async () => {
    if (!exclusao) return;
    try {
      if (exclusao.tipo === 'tag') await gestao.excluirTag.mutateAsync(exclusao.item.id);
      else await gestao.excluirCategoria.mutateAsync(exclusao.item.id);
      setExclusao(null);
    } catch {
      // O erro já foi mostrado pelo hook; o diálogo de confirmação fica aberto.
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Spinner size={32} label="Carregando tags" />
      </div>
    );
  }
  if (isError && tags.length === 0) {
    return <p className="py-6 text-center text-sm text-muted-foreground">Não foi possível carregar as tags.</p>;
  }

  if (edicao?.tipo === 'tag') {
    return (
      <TagForm
        inicial={edicao.tag}
        escritorioId={edicao.escritorioId}
        equipeId={edicao.equipeId}
        categoriaId={edicao.categoriaId}
        escopos={escopos}
        categorias={categorias}
        salvando={gestao.criarTag.isPending || gestao.editarTag.isPending}
        onSalvar={salvarTag}
        onCancelar={() => setEdicao(null)}
      />
    );
  }
  if (edicao?.tipo === 'categoria') {
    return (
      <CategoriaForm
        inicial={edicao.categoria}
        escritorioId={edicao.escritorioId}
        escopos={escoposDaCategoria}
        salvando={gestao.criarCategoria.isPending || gestao.editarCategoria.isPending}
        onSalvar={salvarCategoria}
        onCancelar={() => setEdicao(null)}
      />
    );
  }

  const vazio = tags.length === 0 && categorias.length === 0;

  return (
    <div className="space-y-3">
      {vazio && (
        <p className="py-4 text-center text-sm text-muted-foreground">
          Você ainda não tem tags. Crie a primeira para marcar processos, clientes, modelos e documentos.
        </p>
      )}

      {!vazio &&
        secoes.map((secao) => (
          <section key={secao.chave} aria-label={secao.titulo} className="space-y-2">
            {secoes.length > 1 && (
              <h3 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{secao.titulo}</h3>
            )}
            {secao.grupos.length === 0 && (
              <p className="text-xs text-muted-foreground">Nenhuma tag aqui ainda.</p>
            )}
            {secao.grupos.map((grupo) => (
              <div key={grupo.categoria?.id ?? 'sem'} className="rounded-md border">
                <div className="flex items-center justify-between gap-2 border-b bg-muted/40 px-3 py-1">
                  <span className="truncate text-sm font-medium">{grupo.categoria?.nome ?? 'Sem categoria'}</span>
                  {grupo.categoria?.pode_gerir && !somenteLeitura && (
                    <span className="flex shrink-0 items-center">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-11 w-11 sm:h-9 sm:w-9"
                        disabled={!podeGerir}
                        onClick={() => setEdicao({ tipo: 'categoria', categoria: grupo.categoria!, escritorioId: secao.escritorioId })}
                        aria-label={`Editar a categoria ${grupo.categoria.nome}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-11 w-11 text-destructive sm:h-9 sm:w-9"
                        disabled={!podeGerir}
                        onClick={() => setExclusao({ tipo: 'categoria', item: grupo.categoria! })}
                        aria-label={`Excluir a categoria ${grupo.categoria.nome}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </span>
                  )}
                </div>
                {grupo.tags.length === 0 && (
                  <p className="px-3 py-2 text-xs text-muted-foreground">Nenhuma tag nesta categoria.</p>
                )}
                <ul>
                  {grupo.tags.map((tag) => {
                    const marcada = marcadas.has(tag.id);
                    return (
                      <li key={tag.id} className="flex items-center gap-1 pr-1">
                        <Button
                          variant="ghost"
                          role="checkbox"
                          aria-checked={marcada}
                          disabled={somenteLeitura}
                          onClick={() => onAlternar(tag)}
                          className="h-11 min-w-0 flex-1 justify-start gap-3 rounded-none px-3"
                        >
                          <span
                            className={cn(
                              'flex h-5 w-5 shrink-0 items-center justify-center rounded border',
                              marcada ? 'border-primary bg-primary text-primary-foreground' : 'border-muted-foreground/50',
                            )}
                            aria-hidden
                          >
                            {marcada && <Check className="h-3.5 w-3.5" />}
                          </span>
                          <TagChip tag={tag} className="max-w-[14rem]" />
                        </Button>
                        {tag.pode_gerir && !somenteLeitura && (
                          <>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-11 w-11 shrink-0 sm:h-9 sm:w-9"
                              disabled={!podeGerir}
                              onClick={() =>
                                setEdicao({
                                  tipo: 'tag',
                                  tag,
                                  escritorioId: tag.escritorio_id ?? null,
                                  equipeId: tag.equipe_id ?? null,
                                  categoriaId: tag.categoria_id ?? null,
                                })
                              }
                              aria-label={`Editar a tag ${tag.nome}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-11 w-11 shrink-0 text-destructive sm:h-9 sm:w-9"
                              disabled={!podeGerir}
                              onClick={() => setExclusao({ tipo: 'tag', item: tag })}
                              aria-label={`Excluir a tag ${tag.nome}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </section>
        ))}

      {!somenteLeitura && (
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            className="h-11 sm:h-9"
            disabled={!podeGerir}
            onClick={() => setEdicao({ tipo: 'tag', escritorioId: escopoPadrao(escopos), categoriaId: null })}
          >
            <Plus className="mr-2 h-4 w-4" />
            Criar nova tag
          </Button>
          <Button
            variant="ghost"
            className="h-11 sm:h-9"
            disabled={!podeGerir}
            onClick={() => setEdicao({ tipo: 'categoria', escritorioId: escopoPadrao(escopos) })}
          >
            <FolderPlus className="mr-2 h-4 w-4" />
            Nova categoria
          </Button>
        </div>
      )}
      {!somenteLeitura && !isOnline && (
        <p className="text-xs text-muted-foreground">
          Sem conexão: dá para marcar e desmarcar; criar, editar e excluir tags volta quando a conexão voltar.
        </p>
      )}

      <ConfirmDialog
        open={!!exclusao}
        title={exclusao?.tipo === 'categoria' ? 'Excluir categoria?' : 'Excluir tag?'}
        message={
          exclusao?.tipo === 'categoria'
            ? `As tags de "${exclusao.item.nome}" vão para "Sem categoria".`
            : `"${exclusao?.item.nome ?? ''}" sai de todos os registros em que está${alcanceDaExclusao(exclusao?.item)}.`
        }
        confirmLabel="Excluir"
        confirmColor="error"
        loading={gestao.excluirTag.isPending || gestao.excluirCategoria.isPending}
        onConfirm={confirmarExclusao}
        onCancel={() => setExclusao(null)}
      />
    </div>
  );
}
